import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { MagnifyingGlassIcon, DocumentArrowDownIcon, UserIcon, EnvelopeIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { feeAPI, studentAPI, batchAPI, paymentAPI } from '../../services/api';
import { formatIndianCurrency, formatDateToIST } from '../../utils/dateUtils';
import InvoiceModal from '../InvoiceModal';

const PAGE_SIZE = 20;

const Payments = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  // Fetch payments with server-side pagination
  const { data: paymentsData, isLoading: paymentsLoading, error: paymentsError, isFetching } = useQuery({
    queryKey: ['allPayments', currentPage],
    queryFn: () => paymentAPI.getAll(currentPage * PAGE_SIZE, PAGE_SIZE).then(res => res.data),
    keepPreviousData: true,
    onError: (error) => {
      console.error('Error fetching payments:', error);
    }
  });

  const allPayments = paymentsData || [];
  const hasMorePages = allPayments.length === PAGE_SIZE;

  // Fetch students for filtering
  const { data: students, isLoading: studentsLoading } = useQuery({
    queryKey: ['students'],
    queryFn: () => studentAPI.getAll(0, 1000, true).then(res => res.data),
  });

  // Fetch batches for filtering
  const { data: batches, isLoading: batchesLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => batchAPI.getAll(0, 500, true).then(res => res.data),
  });

  // Filter payments based on search and filters
  const filteredPayments = React.useMemo(() => {
    let filtered = allPayments || [];

    // Search by payment ID, student name, or batch name
    if (searchTerm) {
      filtered = filtered.filter(payment => {
        const studentName = payment.student?.first_name + ' ' + payment.student?.last_name || '';
        const batchName = payment.batch?.batch_name || '';
        const paymentId = payment.payment_id || '';
        
        return studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               batchName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               paymentId.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Filter by student
    if (selectedStudent) {
      filtered = filtered.filter(payment => 
        payment.student?.id === selectedStudent || payment.student?._id === selectedStudent
      );
    }

    // Filter by batch
    if (selectedBatch) {
      filtered = filtered.filter(payment => 
        payment.batch?.id === selectedBatch || payment.batch?._id === selectedBatch
      );
    }

    // Filter by payment method
    if (selectedPaymentMethod) {
      filtered = filtered.filter(payment => 
        payment.payment_method === selectedPaymentMethod
      );
    }

    // Filter by status
    if (selectedStatus) {
      filtered = filtered.filter(payment => 
        payment.status === selectedStatus
      );
    }

    return filtered;
  }, [allPayments, searchTerm, selectedStudent, selectedBatch, selectedPaymentMethod, selectedStatus]);

  // With server-side pagination, filtered payments ARE the paginated payments
  const paginatedPayments = filteredPayments;
  const isPageLoading = isFetching && !paymentsLoading;

  // Calculate totals
  const totalAmount = React.useMemo(() => {
    return filteredPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
  }, [filteredPayments]);

  const [generatingInvoiceId, setGeneratingInvoiceId] = useState(null);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);

  const generateInvoiceMutation = useMutation({
    mutationFn: (paymentId) => feeAPI.generateInvoice(paymentId, true),
    onSuccess: (response) => {
      // Success is handled by the modal
      console.log('Invoice generated successfully');
      setGeneratingInvoiceId(null);
    },
    onError: (error) => {
      console.error('Error generating invoice:', error);
      setGeneratingInvoiceId(null);
    },
  });

  const handleGenerateInvoice = (payment) => {
    setSelectedPayment(payment);
    setShowInvoiceModal(true);
  };

  const getStudentName = (student) => {
    if (!student) return 'Unknown Student';
    return `${student.first_name || ''} ${student.last_name || ''}`.trim() || 'Unknown Student';
  };

  const getBatchName = (batch) => {
    if (!batch) return 'Unknown Batch';
    return batch.batch_name || 'Unknown Batch';
  };

  const getPaymentMethodLabel = (method) => {
    const methods = {
      cash: 'Cash',
      online: 'Online Transfer',
      cheque: 'Cheque',
      card: 'Card'
    };
    return methods[method] || method;
  };

  const getStatusColor = (status) => {
    const colors = {
      completed: 'bg-green-900/30 text-green-300 border border-green-700',
      pending: 'bg-yellow-900/30 text-yellow-300 border border-yellow-700',
      failed: 'bg-red-900/30 text-red-300 border border-red-700',
      cancelled: 'bg-gray-900/30 text-gray-300 border border-gray-700'
    };
    return colors[status] || 'bg-gray-900/30 text-gray-300 border border-gray-700';
  };

  const isLoading = paymentsLoading || studentsLoading || batchesLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (paymentsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="text-red-500 text-xl mb-4">Error loading payments</div>
          <div className="text-gray-400 text-sm">{paymentsError.message}</div>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">Payments</h1>
          <p className="text-gray-300 mt-1 sm:mt-2 text-sm sm:text-base">View and manage all payment records</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-right">
            <p className="text-xs sm:text-sm text-gray-400">Total Amount</p>
            <p className="text-lg sm:text-2xl font-bold text-white">{formatIndianCurrency(totalAmount)}</p>
          </div>
        </div>
      </div>



      {/* Filters */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-4 sm:p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Search */}
          <div className="sm:col-span-2 lg:col-span-2">
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Search</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-4 w-4 sm:h-5 sm:w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="block w-full pl-8 sm:pl-10 pr-3 py-2 border border-gray-600 rounded-md leading-5 bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:placeholder-gray-500 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm sm:text-base"
                placeholder="Search payments..."
              />
            </div>
          </div>

          {/* Student Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Student</label>
            <select
              value={selectedStudent}
              onChange={(e) => setSelectedStudent(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-700 text-white text-sm sm:text-base"
            >
              <option value="">All Students</option>
              {students?.map((student) => (
                <option key={student._id || student.id} value={student._id || student.id}>
                  {student.first_name} {student.last_name}
                </option>
              ))}
            </select>
          </div>

          {/* Batch Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Batch</label>
            <select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-700 text-white text-sm sm:text-base"
            >
              <option value="">All Batches</option>
              {batches?.map((batch) => (
                <option key={batch._id || batch.id} value={batch._id || batch.id}>
                  {batch.batch_name}
                </option>
              ))}
            </select>
          </div>

          {/* Payment Method Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Method</label>
            <select
              value={selectedPaymentMethod}
              onChange={(e) => setSelectedPaymentMethod(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-700 text-white text-sm sm:text-base"
            >
              <option value="">All Methods</option>
              <option value="cash">Cash</option>
              <option value="online">Online</option>
              <option value="cheque">Cheque</option>
              <option value="card">Card</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-300 mb-1 sm:mb-2">Status</label>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="block w-full px-3 py-2 border border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 bg-gray-700 text-white text-sm sm:text-base"
            >
              <option value="">All Status</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </div>
        </div>
      </div>

      {/* Payments List */}
      <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 overflow-hidden">
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-700">
          <h2 className="text-base sm:text-lg font-semibold text-white">
            {filteredPayments.length} Payment{filteredPayments.length !== 1 ? 's' : ''}
          </h2>
        </div>
        
        {filteredPayments.length === 0 ? (
          <div className="text-center py-8 sm:py-12">
            <DocumentArrowDownIcon className="mx-auto h-8 w-8 sm:h-12 sm:w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-300">No payments found</h3>
            <p className="mt-1 text-xs sm:text-sm text-gray-400">
              {searchTerm || selectedStudent || selectedBatch || selectedPaymentMethod || selectedStatus ? 'Try adjusting your filters.' : 'No payment records available.'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-700">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Payment ID
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Student
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Batch
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Amount
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden md:table-cell">
                    Method
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden lg:table-cell">
                    Date
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider hidden sm:table-cell">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-2 sm:py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-gray-800 divide-y divide-gray-700">
                {paginatedPayments.map((payment) => (
                  <tr key={payment._id || payment.id} className="hover:bg-gray-700">
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                      <div className="font-medium text-white">
                        {payment.payment_id || 'N/A'}
                      </div>

                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-6 w-6 sm:h-8 sm:w-8">
                          <div className="h-6 w-6 sm:h-8 sm:w-8 rounded-full bg-blue-600 flex items-center justify-center">
                            <UserIcon className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
                          </div>
                        </div>
                        <div className="ml-2 sm:ml-3">
                          <div className="text-sm font-medium text-white">
                            {getStudentName(payment.student)}
                          </div>
                          {/* Mobile-only payment info */}
                          <div className="sm:hidden text-xs text-gray-400 mt-1">
                            {formatIndianCurrency(payment.amount || 0)} • {getPaymentMethodLabel(payment.payment_method)}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                      <div>{getBatchName(payment.batch)}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium text-white hidden lg:table-cell">
                      <div>{formatIndianCurrency(payment.amount || 0)}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden md:table-cell">
                      <div>{getPaymentMethodLabel(payment.payment_method)}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden lg:table-cell">
                      <div>{formatDateToIST(payment.payment_date)}</div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm text-gray-300 hidden sm:table-cell">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payment.status)}`}>
                        {payment.status}
                      </span>
                    </td>
                    <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={() => handleGenerateInvoice(payment)}
                          disabled={generatingInvoiceId === (payment._id || payment.id)}
                          className="p-1 sm:p-2 text-blue-400 hover:text-blue-300 rounded-md hover:bg-gray-700 transition-colors disabled:opacity-50"
                          title="Generate Invoice"
                        >
                          <EnvelopeIcon className="h-4 w-4 sm:h-5 sm:w-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {paginatedPayments.length > 0 && (
        <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-400">
              Page {currentPage + 1} • Showing {paginatedPayments.length} results {hasMorePages ? '' : '(last page)'}
              {isPageLoading && <span className="ml-2 text-yellow-400">Loading...</span>}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(0, currentPage - 1))}
                disabled={currentPage === 0 || isPageLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeftIcon className="h-4 w-4 mr-1" />
                Previous
              </button>
              <span className="px-3 py-2 text-sm text-gray-300 bg-gray-700 border border-gray-600 rounded-md">
                {currentPage + 1}
              </span>
              <button
                onClick={() => setCurrentPage(currentPage + 1)}
                disabled={!hasMorePages || isPageLoading}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 rounded-md hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Next
                <ChevronRightIcon className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal */}
      <InvoiceModal
        isOpen={showInvoiceModal}
        onClose={() => setShowInvoiceModal(false)}
        paymentData={selectedPayment}
        generateInvoiceMutation={generateInvoiceMutation}
      />
    </div>
  );
};

export default Payments; 